"use client";

import { Moon, Sun } from "lucide-react";
import { LOCALES } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { AccessibilityMenu } from "./accessibility-menu";
import { CountryFlag } from "./country-flag";
import { usePrefs } from "./prefs-provider";

/**
 * Language · theme · accessibility. Same control everywhere (marketing header,
 * app header, admin, auth pages) so users always find it in the same place.
 */
export function PrefsControls({ compact = false, className }: { compact?: boolean; className?: string }) {
  const { locale, theme, setLocale, toggleTheme, t } = usePrefs();
  const h = compact ? "h-9" : "h-10";

  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      <div
        role="group"
        aria-label={t.nav.language}
        className={cn("flex overflow-hidden rounded-xl border border-line bg-surface p-0.5", h)}
      >
        {LOCALES.map((l) => {
          const active = locale === l.id;
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => setLocale(l.id)}
              lang={l.id}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-[0.6rem] px-2 text-xs font-bold transition-colors",
                active ? "bg-brand text-on-brand" : "text-ink-2 hover:bg-surface-2 hover:text-ink",
              )}
              aria-pressed={active}
              title={l.label}
            >
              <CountryFlag iso={l.country} className="h-3 w-[1.125rem] rounded-[2px]" />
              <span>{l.abbr}</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={toggleTheme}
        className={cn(
          "inline-flex w-9 items-center justify-center rounded-xl border border-line bg-surface text-ink transition-colors hover:bg-surface-2",
          h,
        )}
        title={theme === "dark" ? t.nav.themeLight : t.nav.themeDark}
        aria-label={theme === "dark" ? t.nav.themeLight : t.nav.themeDark}
      >
        {theme === "dark" ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
      </button>
      <AccessibilityMenu compact={compact} />
    </div>
  );
}
